import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

export const Cart = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const { user } = useAuth();
  
  const subtotal = getCartTotal();
  const total = subtotal;

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-purple-25" data-testid="empty-cart">
        <div className="text-center">
          <h2 className="text-3xl font-heading font-light mb-4">Your Cart is Empty</h2>
          <p className="text-stone-600 mb-8">Add some products to get started!</p>
          <Button onClick={() => navigate('/shop')} className="rounded-full" data-testid="shop-now-button">
            Shop Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 lg:px-12 bg-purple-25" data-testid="cart-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-heading font-light mb-12" data-testid="cart-title">
          Shopping Cart
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => (
              <Card key={`${item.product_id}-${item.size}`} className="p-6" data-testid={`cart-item-${index}`}>
                <div className="flex gap-6">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-white flex-shrink-0 flex items-center justify-center p-2">
                    <img
                      src={item.image_url}
                      alt={item.product_name}
                      className="w-full h-full object-contain"
                      data-testid="item-image"
                    />
                  </div>

                  <div className="flex-1">
                    <h3 className="font-heading text-xl mb-1" data-testid="item-name">
                      {item.product_name}
                    </h3>
                    <p className="text-sm text-stone-600 mb-4" data-testid="item-size">
                      Size: {item.size}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                          data-testid="decrease-quantity"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center" data-testid="item-quantity">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                          data-testid="increase-quantity"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-4">
                        <p className="text-xl font-heading" data-testid="item-price">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromCart(item.product_id, item.size)}
                          data-testid="remove-item"
                        >
                          <Trash2 className="h-5 w-5 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-2xl font-heading mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-stone-600">Subtotal</span>
                  <span data-testid="subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Shipping</span>
                  <span data-testid="shipping" className="text-stone-500">-</span>
                </div>
                <div className="border-t border-stone-200 pt-4 flex justify-between text-xl font-heading">
                  <span>Total</span>
                  <span data-testid="total">${total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                className="w-full rounded-full mb-4"
                size="lg"
                onClick={() => navigate('/checkout')}
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </Button>

              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate('/shop')}
                data-testid="continue-shopping"
              >
                Continue Shopping
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};